import React from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, StyleSheet } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import theme from '@themes/theme';

interface ProjectOnboardingCardProps {
  projectId: string;
  projectName: string;
  onGoToProject?: (projectId: string) => void;
  requestSent?: boolean;
  onDismiss?: () => void;
}

const FEATURE_ITEMS = [
  { icon: 'Hash', key: 'tasks' },
  { icon: 'Image', key: 'media' },
  { icon: 'MessageSquare', key: 'chat' },
  { icon: 'Activity1', key: 'updates' },
] as const;

type FeatureRowProps = {
  icon: (typeof FEATURE_ITEMS)[number]['icon'];
  title: string;
  subtitle: string;
};

function FeatureRow({ icon, title, subtitle }: Readonly<FeatureRowProps>) {
  return (
    <Box flexDirection='row' alignItems='center'>
      <Box
        width={40}
        height={40}
        backgroundColor='grey01'
        borderRadius='xs'
        alignItems='center'
        justifyContent='center'
        marginRight='s'>
        <Icon name={icon} variant='s' color='grey04' />
      </Box>
      <Box flex={1}>
        <Text variant='webBody' color='textPrimary'>
          {title}
        </Text>
        <Text variant='webBodyHintSubline' marginTop='xxs'>
          {subtitle}
        </Text>
      </Box>
    </Box>
  );
}

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.eloveit.TaskTag';
const APP_STORE_URL = 'https://apps.apple.com/app/id6448815209';

function getAppStoreUrl(): string {
  if (typeof window === 'undefined') return PLAY_STORE_URL;
  const ua = window.navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) ? APP_STORE_URL : PLAY_STORE_URL;
}

const isMobileBrowser =
  typeof window !== 'undefined' &&
  /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    window.navigator?.userAgent ?? ''
  );

export function ProjectOnboardingCard({
  projectId,
  projectName,
  onGoToProject,
  requestSent = false,
  onDismiss,
}: Readonly<ProjectOnboardingCardProps>) {
  const { t } = useTranslation('models');

  if (requestSent) {
    return (
      <Box>
        {/* Drag handle */}
        <Box style={styles.sheetHandle} />

        {/* Centered content */}
        <Box alignItems='center' paddingVertical='xs' marginBottom='l'>
          <Box
            width={56}
            height={56}
            backgroundColor='grey02'
            alignItems='center'
            justifyContent='center'
            marginBottom='s'
            style={{ borderRadius: 28 }}>
            <Icon name='Clock' width={28} height={28} color='grey05' />
          </Box>
          <Text
            variant='labelLarge'
            color='textPrimary'
            marginBottom='xs'
            style={{ textAlign: 'center' }}>
            {t('projects.onboardingCard.requestSent')}
          </Text>
          <Text
            variant='webBodySecondary'
            color='textSecondary'
            style={{ textAlign: 'center', lineHeight: 22 }}>
            {t('projects.onboardingCard.notifiedWhenApproved')}
          </Text>
        </Box>

        {/* Got It button */}
        <Button
          onPress={onDismiss}
          fullWidth
          backgroundColor='black'
          borderRadius='s'
          accessibilityLabel={t('projects.onboardingCard.gotIt')}>
          <Text variant='labelSmall' color='white'>
            {t('projects.onboardingCard.gotIt')}
          </Text>
        </Button>
      </Box>
    );
  }

  return (
    <Box
      backgroundColor='white'
      borderRadius='m'
      padding='l'
      style={styles.card}>
      {onDismiss && (
        <Box style={styles.dismiss}>
          <Icon
            name='X'
            variant='s'
            color='grey04'
            testId='projects.onboardingCard.dismiss'
            accessibilityLabel={t('projects.onboardingCard.dismiss')}
            onPress={onDismiss}
          />
        </Box>
      )}
      <Box
        flexDirection='row'
        alignItems='center'
        marginBottom='l'
        paddingRight={onDismiss ? 'm' : 'none'}>
        <Box
          width={40}
          height={40}
          backgroundColor='purple'
          borderRadius='xs'
          alignItems='center'
          justifyContent='center'
          marginRight='xs'>
          <Icon name='Briefcase' variant='l' color='white' />
        </Box>
        <Box flex={1}>
          <Text variant='labelEmphasizedChatDetialTitle' color='textPrimary'>
            {projectName}
          </Text>
          <Text variant='webBodyHintSubline' marginTop='xxs'>
            {t('projects.onboardingCard.addedToProject')}
          </Text>
        </Box>
      </Box>

      <Text variant='webBody' color='textPrimary' marginBottom='m'>
        {t('projects.onboardingCard.whatYouCanDo')}
      </Text>
      {FEATURE_ITEMS.map((item, index) => (
        <Box key={item.icon} marginTop={index > 0 ? 'm' : 'none'}>
          <FeatureRow
            icon={item.icon}
            title={t(`projects.onboardingCard.features.${item.key}.title`)}
            subtitle={t(
              `projects.onboardingCard.features.${item.key}.subtitle`
            )}
          />
        </Box>
      ))}

      <Button
        onPress={() => onGoToProject?.(projectId)}
        fullWidth
        isSmall={isMobileBrowser}
        backgroundColor='black'
        borderRadius='xs'
        marginTop='l'
        // TA-10891: labelled "Got it!" — it still opens the project.
        accessibilityLabel={t('shared:projectInviteOnboarding.gotIt')}>
        <Text variant='labelSmall' color='white'>
          {t('shared:projectInviteOnboarding.gotIt')}
        </Text>
      </Button>

      {isMobileBrowser && (
        <Button
          onPress={() => Linking.openURL(getAppStoreUrl())}
          fullWidth
          isSmall
          backgroundColor='white'
          borderRadius='xs'
          borderColor='black'
          marginTop='s'
          accessibilityLabel='Download the app'
          prefix={<Icon variant='s' name='Download' color='black' />}>
          <Text variant='labelSmall' color='black' ml='xs'>
            Download the App
          </Text>
        </Button>
      )}
    </Box>
  );
}

const styles = StyleSheet.create({
  card: {
    // Deep enough to separate the card both from the app content and from the
    // stacked cards peeking out beneath it.
    shadowColor: theme.colors.black,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.22,
    shadowRadius: 20,
    elevation: 8,
  },
  dismiss: {
    position: 'absolute',
    top: theme.spacing.m,
    right: theme.spacing.m,
    zIndex: 1,
  },
  sheetHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.colors.grey03,
    alignSelf: 'center',
    marginBottom: theme.spacing.m,
  },
});
